// Nav.tsx
import { Link as RouterLink, useLocation } from "@tanstack/react-router";
import { HStack, Link as ChakraLink, Text, Box } from "@chakra-ui/react";
import { IconComponent } from "@/components";
import { forwardRef, useMemo } from "react";

interface NavItem {
  name: string;
  url: string;
  icon: string;
  isActive: boolean;
}

interface NavProps {
  direction: "vertical" | "horizontal";
  navigation: NavItem[];
}

const CustomRouterLink = forwardRef<HTMLAnchorElement, any>(
  ({ isActive, to, ...props }, ref) => {
    const location = useLocation();
    
    // Forza il re-render quando cambia la location
    const currentIsActive = useMemo(() => {
      const pathPattern = to.toString()
        .replace(/\{[^}]+\}/g, '[^/]+')
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      const pathRegex = new RegExp(`^${pathPattern}$`);
      return pathRegex.test(location.pathname);
    }, [location.pathname, to]);

    return (
      <ChakraLink
        ref={ref}
        as={RouterLink}
        to={to}
        data-status={currentIsActive ? "active" : undefined}
        aria-current={currentIsActive ? "page" : undefined}
        className={`chakra-link ${currentIsActive ? "active" : ""}`}
        bg={currentIsActive ? "gray.100" : "transparent"}
        color={currentIsActive ? "primary.500" : "gray.600"}
        {...props}
      />
    );
  }
);

export const Nav = ({ direction, navigation }: NavProps) => {
  return (
    <HStack
      as="nav"
      flexDir={direction === "vertical" ? "column" : "row"}
      spacing={2}
    >
      {navigation.map((item) => (
        <CustomRouterLink
          key={item.name}
          to={item.url}
          preload="intent"
          fontWeight="medium"
          fontSize="sm"
          _hover={{
            bg: "gray.100",
            color: "inherit",
            textDecoration: "none",
          }}
          _active={{
            bg: "gray.100",
            color: "primary.500",
          }}
          _focus={{
            bg: "gray.100",
            color: "inherit",
            textDecoration: "none",
          }}
          textDecoration="none"
          w="150px"
          h="48px"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          //flexDirection="column"
          borderRadius="18px"
          transition="ease all 0.4s"
          textAlign="center"
        >
          <Box as="span" w="50px" display="flex" justifyContent="center" fontSize="1.5rem">
            <IconComponent type={item.icon} />
          </Box>
          <Text fontSize="sm">
            {item.name}
          </Text>
        </CustomRouterLink>
      ))}
    </HStack>
  );
};